import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import styles from "./Mobile.module.css";

/**
 * /mobile — install + native-app roadmap page.
 *
 * The app is a real installable PWA (vite-plugin-pwa generates the manifest
 * + service worker), so the "Add to home screen" instructions below describe
 * behaviour that works today. The "Native apps" section is explicitly framed
 * as future work — no fake store links, the badges are disabled and labeled
 * "coming soon".
 *
 * Client-rendered, default export. App.tsx wires the route.
 */
export default function Mobile() {
  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>Mobile</p>
            <h1 className={styles.title}>Run your social OS from your pocket.</h1>
            <p className={styles.lead}>
              Notamen installs straight from your browser as a Progressive
              Web App — a full-screen, home-screen icon with no app store
              required. Approve drafts, check the calendar, and watch publishing
              status wherever you are.
            </p>
          </div>
        </section>

        <section className={styles.installSection}>
          <div className={styles.installInner}>
            <h2 className={styles.sectionTitle}>Add to your home screen</h2>
            <p className={styles.sectionLead}>
              Installing takes a few taps and works on both iOS and Android. Once
              installed, it opens like a native app — full screen, in its own
              window.
            </p>

            <div className={styles.cards}>
              <article className={styles.card}>
                <header className={styles.cardHeader}>
                  <span className={styles.platformTag}>iOS</span>
                  <h3 className={styles.cardTitle}>Safari on iPhone &amp; iPad</h3>
                </header>
                <ol className={styles.steps}>
                  <li className={styles.step}>
                    Open{" "}
                    <code className={styles.code}>
                      socialmediamanager.pheonixapps.com
                    </code>{" "}
                    in Safari.
                  </li>
                  <li className={styles.step}>
                    Tap the <strong>Share</strong> button (the square with an
                    up-arrow) in the toolbar.
                  </li>
                  <li className={styles.step}>
                    Scroll down and tap <strong>Add to Home Screen</strong>.
                  </li>
                  <li className={styles.step}>
                    Confirm the name, then tap <strong>Add</strong>. The icon
                    lands on your home screen.
                  </li>
                </ol>
                <p className={styles.note}>
                  Add to Home Screen is a Safari feature — Chrome and Firefox on
                  iOS can't install web apps yet, so use Safari on Apple devices.
                </p>
              </article>

              <article className={styles.card}>
                <header className={styles.cardHeader}>
                  <span className={styles.platformTag}>Android</span>
                  <h3 className={styles.cardTitle}>Chrome on Android</h3>
                </header>
                <ol className={styles.steps}>
                  <li className={styles.step}>
                    Open{" "}
                    <code className={styles.code}>
                      socialmediamanager.pheonixapps.com
                    </code>{" "}
                    in Chrome.
                  </li>
                  <li className={styles.step}>
                    Tap the <strong>⋮</strong> menu in the top-right corner.
                  </li>
                  <li className={styles.step}>
                    Tap <strong>Install app</strong> (or{" "}
                    <strong>Add to Home screen</strong> on older versions).
                  </li>
                  <li className={styles.step}>
                    Confirm with <strong>Install</strong>. You may also see an
                    install prompt appear automatically in the address bar.
                  </li>
                </ol>
                <p className={styles.note}>
                  Edge, Samsung Internet, and other Chromium browsers on Android
                  follow the same menu → Install app flow.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.roadmapSection}>
          <div className={styles.roadmapInner}>
            <span className={styles.roadmapBadge}>On the roadmap</span>
            <h2 className={styles.sectionTitle}>Native apps</h2>
            <p className={styles.sectionLead}>
              Dedicated iOS and Android apps with push notifications for approvals
              and publish results are planned, but they aren&apos;t available yet.
              We&apos;re not listing fake download links — when the native apps
              ship, the buttons below will go live and we&apos;ll email everyone on
              the waitlist.
            </p>

            <div className={styles.storeBadges} aria-label="Native app stores — coming soon">
              <div className={styles.storeBadge} aria-disabled="true">
                <span className={styles.storeGlyph} aria-hidden="true">
                  iOS
                </span>
                <span className={styles.storeText}>
                  <span className={styles.storeSmall}>Coming soon to the</span>
                  <span className={styles.storeBig}>App Store</span>
                </span>
                <span className={styles.comingSoonTag}>Coming soon</span>
              </div>

              <div className={styles.storeBadge} aria-disabled="true">
                <span className={styles.storeGlyph} aria-hidden="true">
                  ▶
                </span>
                <span className={styles.storeText}>
                  <span className={styles.storeSmall}>Coming soon to</span>
                  <span className={styles.storeBig}>Google Play</span>
                </span>
                <span className={styles.comingSoonTag}>Coming soon</span>
              </div>
            </div>

            <p className={styles.roadmapNote}>
              In the meantime, the installable web app above gives you the
              full-screen, home-screen experience on every modern phone today.
            </p>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}
